import { forwardRef, useState } from "react";
import {
  defaultGroupForView,
  type FindingQueueQuery,
  type FindingsScope,
  findingsScopeSchema,
  HOTSPOT_UNLINKED_KEY,
  parseGroupingKey,
} from "@iamu/shared";
import { Plus, RotateCcw, SlidersHorizontal, X } from "lucide-react";
import { FilterBar, FilterPill } from "@/components/ui/filter-bar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SortDropdown } from "@/components/ui/sort-dropdown";
import { trpc } from "@/lib/trpc";

const SCOPE_KEYS = Object.keys(
  findingsScopeSchema.shape,
) as (keyof FindingsScope)[];

const ALL = "all";

const SORT_OPTIONS = [
  { value: "priority", label: "Priority" },
  { value: "last_seen", label: "Last seen" },
  { value: "first_seen", label: "First seen" },
  { value: "observations", label: "Most observed" },
  { value: "severity", label: "Severity" },
] as const;

const GROUP_OPTIONS = [
  { value: "none", label: "No grouping" },
  { value: "capability", label: "Group by capability" },
  { value: "outline", label: "Group by scenario" },
  { value: "scenario", label: "Group by scenario and persona" },
  { value: "persona", label: "Group by persona" },
  { value: "page", label: "Group by page" },
  { value: "state", label: "Group by state" },
] as const;

interface QueueFiltersProps {
  projectId: string;
  query: FindingQueueQuery;
  onChange: (next: FindingQueueQuery) => void;
  /** `hotspots`: only the filters the hotspot map shares (no type, group, sort). */
  variant?: "queue" | "hotspots";
}

/**
 * Persona, capability and severity are the page-wide scope's
 * (`FindingsScopeBar`); this bar holds the view's own filters.
 */
type SecondaryFilterKey = "judge" | "request";

/** The search input is forwarded so `/` can focus it. */
export const QueueFilters = forwardRef<HTMLInputElement, QueueFiltersProps>(
  function QueueFilters(
    { projectId, query, onChange, variant = "queue" },
    searchRef,
  ) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const queue = variant === "queue";
    const { data: personas } = trpc.persona.list.useQuery({ projectId });
    const { data: capabilities } = trpc.capability.list.useQuery({
      projectId,
    });
    const set = (patch: Partial<FindingQueueQuery>) =>
      onChange({ ...query, ...patch });

    // Available secondary filters for "+ Filter" dropdown
    const availableFilters: Array<{ id: SecondaryFilterKey; label: string }> =
      [];
    if (!query.judgeCited) {
      availableFilters.push({ id: "judge", label: "Judge citation" });
    }
    if (queue && !query.request) {
      availableFilters.push({ id: "request", label: "Has open request" });
    }

    const handleAddFilter = (id: SecondaryFilterKey) => {
      switch (id) {
        case "judge":
          set({ judgeCited: true });
          break;
        case "request":
          set({ request: "open" });
          break;
      }
    };

    const activeFilterCount = [
      query.type && query.type !== "problems",
      query.judgeCited,
      query.request,
      query.area,
    ].filter(Boolean).length;

    const hasActiveFilters = Boolean(query.search || activeFilterCount > 0);

    // Reset clears this view's filters; the page-wide scope stays.
    const handleResetAll = () => {
      const scope = Object.fromEntries(
        SCOPE_KEYS.map((k) => [k, query[k]]),
      ) as Partial<FindingQueueQuery>;
      onChange({
        ...scope,
        view: query.view,
        group: query.group,
        sort: query.sort,
      });
    };

    return (
      <div className="space-y-2">
        {/* Mobile compact filter bar (<md) */}
        <div className="space-y-2 md:hidden">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <label htmlFor="queue-search-mobile" className="sr-only">
                Search findings
              </label>
              <Input
                id="queue-search-mobile"
                type="search"
                placeholder="Search findings..."
                defaultValue={query.search ?? ""}
                key={`mobile-${query.search ?? ""}`}
                className="h-9 rounded-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    set({ search: e.currentTarget.value.trim() || undefined });
                  }
                  if (e.key === "Escape") e.currentTarget.blur();
                }}
                onBlur={(e) => {
                  const v = e.currentTarget.value.trim() || undefined;
                  if (v !== query.search) set({ search: v });
                }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMobileOpen(true)}
              className="h-9 gap-1.5 shrink-0"
            >
              <SlidersHorizontal className="size-3.5" aria-hidden="true" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <Badge
                  variant="secondary"
                  className="h-5 px-1.5 text-xs font-normal"
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleResetAll}
                aria-label="Reset all filters"
                className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="size-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>

          {query.area && (
            <div className="pt-1">
              <AreaFilterChip
                area={query.area}
                names={[...(personas ?? []), ...(capabilities ?? [])]}
                onRemove={() => set({ area: undefined })}
              />
            </div>
          )}
        </div>

        {/* Mobile bottom filter sheet */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-4 sm:p-6"
          >
            <SheetHeader className="p-0 pb-3 border-b">
              <SheetTitle className="text-base font-semibold">
                Filter findings
              </SheetTitle>
            </SheetHeader>
            <div className="space-y-4 py-4">
              {queue && (
                <div className="space-y-1.5">
                  <Label htmlFor="mobile-filter-type">Finding type</Label>
                  <Select
                    value={query.type ?? "problems"}
                    onValueChange={(v) =>
                      set({
                        type:
                          v === "problems"
                            ? undefined
                            : (v as FindingQueueQuery["type"]),
                      })
                    }
                  >
                    <SelectTrigger id="mobile-filter-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="problems">All problems</SelectItem>
                      <SelectItem value="hinders">Hinders</SelectItem>
                      <SelectItem value="missing">Missing</SelectItem>
                      <SelectItem value="incorrect">Incorrect</SelectItem>
                      <SelectItem value="helps">Strengths</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {queue && (
                <div className="space-y-1.5">
                  <Label htmlFor="mobile-filter-group">Group by</Label>
                  <Select
                    value={query.group ?? defaultGroupForView(query.view)}
                    onValueChange={(v) =>
                      set({ group: v as FindingQueueQuery["group"] })
                    }
                  >
                    <SelectTrigger id="mobile-filter-group" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GROUP_OPTIONS.map((g) => (
                        <SelectItem key={g.value} value={g.value}>
                          {g.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {queue && (
                <div className="space-y-1.5">
                  <Label htmlFor="mobile-filter-sort">Sort by</Label>
                  <Select
                    value={query.sort ?? "priority"}
                    onValueChange={(v) =>
                      set({
                        sort:
                          v === "priority"
                            ? undefined
                            : (v as FindingQueueQuery["sort"]),
                      })
                    }
                  >
                    <SelectTrigger id="mobile-filter-sort" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SORT_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="mobile-filter-judge">Judge citation</Label>
                <Select
                  value={query.judgeCited ? "cited" : ALL}
                  onValueChange={(v) =>
                    set({ judgeCited: v === "cited" ? true : undefined })
                  }
                >
                  <SelectTrigger id="mobile-filter-judge" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All citations</SelectItem>
                    <SelectItem value="cited">Cited by judge</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {queue && (
                <div className="space-y-1.5">
                  <Label htmlFor="mobile-filter-request">Work request</Label>
                  <Select
                    value={query.request ?? ALL}
                    onValueChange={(v) =>
                      set({ request: v === ALL ? undefined : "open" })
                    }
                  >
                    <SelectTrigger
                      id="mobile-filter-request"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>All findings</SelectItem>
                      <SelectItem value="open">Has open request</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <SheetFooter className="p-0 pt-2 border-t flex-row gap-2">
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={handleResetAll}
                  className="flex-1"
                >
                  Reset all
                </Button>
              )}
              <Button className="flex-1" onClick={() => setMobileOpen(false)}>
                Done
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Desktop inline filter bar (>=md) */}
        <div className="hidden md:block">
          <FilterBar className="justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-44 flex-1 sm:max-w-64">
                <label htmlFor="queue-search" className="sr-only">
                  Search findings
                </label>
                <Input
                  id="queue-search"
                  ref={searchRef}
                  type="search"
                  placeholder="Search findings..."
                  defaultValue={query.search ?? ""}
                  key={query.search ?? ""}
                  className="h-9 rounded-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      set({
                        search: e.currentTarget.value.trim() || undefined,
                      });
                    }
                    if (e.key === "Escape") e.currentTarget.blur();
                  }}
                  onBlur={(e) => {
                    const v = e.currentTarget.value.trim() || undefined;
                    if (v !== query.search) set({ search: v });
                  }}
                />
              </div>

              {queue && (
                <FilterPill
                  ariaLabel="Filter by type"
                  value={query.type ?? "problems"}
                  onValueChange={(v) =>
                    set({ type: v === "problems" ? undefined : v })
                  }
                  options={[
                    { value: "problems", label: "All problems" },
                    { value: "hinders", label: "Hinders" },
                    { value: "missing", label: "Missing" },
                    { value: "incorrect", label: "Incorrect" },
                    { value: "helps", label: "Strengths" },
                  ]}
                />
              )}

              {/* Active secondary filters as dismissible pills */}
              {query.judgeCited && (
                <FilterPill
                  ariaLabel="Filter by judge citation"
                  value="cited"
                  onValueChange={(v) =>
                    set({ judgeCited: v === "cited" ? true : undefined })
                  }
                  onRemove={() => set({ judgeCited: undefined })}
                  options={[{ value: "cited", label: "Cited by judge" }]}
                />
              )}

              {queue && query.request && (
                <FilterPill
                  ariaLabel="Filter by work request"
                  value={query.request}
                  onValueChange={(v) =>
                    set({ request: v === "open" ? "open" : undefined })
                  }
                  onRemove={() => set({ request: undefined })}
                  options={[{ value: "open", label: "Has open request" }]}
                />
              )}

              {query.area && (
                <AreaFilterChip
                  area={query.area}
                  names={[...(personas ?? []), ...(capabilities ?? [])]}
                  onRemove={() => set({ area: undefined })}
                />
              )}

              {availableFilters.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 rounded-sm text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="size-3.5" aria-hidden="true" />
                      Filter
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {availableFilters.map((opt) => (
                      <DropdownMenuItem
                        key={opt.id}
                        onClick={() => handleAddFilter(opt.id)}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetAll}
                  className="gap-1 rounded-sm text-xs text-muted-foreground hover:text-foreground"
                >
                  <RotateCcw className="size-3" aria-hidden="true" />
                  Reset
                </Button>
              )}
            </div>

            {queue && (
              <div className="flex items-center gap-2">
                <FilterPill
                  ariaLabel="Group findings"
                  value={query.group ?? defaultGroupForView(query.view)}
                  onValueChange={(v) => set({ group: v })}
                  options={GROUP_OPTIONS}
                />
                <SortDropdown
                  options={SORT_OPTIONS}
                  value={query.sort ?? "priority"}
                  onChange={(v) =>
                    set({ sort: v === "priority" ? undefined : v })
                  }
                />
              </div>
            )}
          </FilterBar>
        </div>
      </div>
    );
  },
);

/** The hotspot area a queue is filtered to (from the map's drill-down). */
function AreaFilterChip({
  area,
  names,
  onRemove,
}: {
  area: string;
  names: Array<{ id: string; name: string }>;
  onRemove: () => void;
}) {
  const key = parseGroupingKey(area);
  const id =
    key?.kind === "capability"
      ? key.capabilityId
      : key?.kind === "persona"
        ? key.personaId
        : null;
  const label =
    area === HOTSPOT_UNLINKED_KEY
      ? "Not cited by any human test"
      : ((id && names.find((n) => n.id === id)?.name) ??
        (key?.kind === "route"
          ? key.routeKey
          : key
            ? key.kind.replace("_", " ")
            : area));
  return (
    <span className="inline-flex h-9 items-center gap-1 rounded-sm border border-input pl-3 text-sm">
      Area: <span className="max-w-[200px] truncate font-medium">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove area filter"
        className="flex h-9 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
      >
        <X className="size-3" aria-hidden="true" />
      </button>
    </span>
  );
}
